import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
} from "@mui/material";
import {
  AlertTriangle,
  PackageCheck,
  ShoppingCart,
  Package,
} from "lucide-react";

const ProductQuickStats = ({ brand, mrp, sp, quantity = 0, inStock }) => {
  // If size information is available, we could parse it
  // For this demo, we'll create sample size distribution
  const sizeQuantities = [];
  let totalQuantity = quantity;

  // Extract size information if available
  if (typeof quantity === "number" && quantity > 0) {
    // Create a sample distribution
    const sizes = ["S", "M", "L", "XL"];
    let remainingQty = quantity;

    sizes.forEach((size, index) => {
      // Random distribution of quantity across sizes
      const sizeQty =
        index === sizes.length - 1
          ? remainingQty
          : Math.floor(Math.random() * (remainingQty / 2)) + 1;

      remainingQty -= sizeQty;

      if (sizeQty > 0) {
        sizeQuantities.push({ size, quantity: sizeQty });
      }
    });
  }

  const lowStockThreshold = 5;
  const maxQuantity =
    sizeQuantities.length > 0
      ? Math.max(...sizeQuantities.map(item => item.quantity))
      : 0;

  // Calculate discount percentage
  const discountPercentage = mrp > 0 ? Math.round(((mrp - sp) / mrp) * 100) : 0;

  return (
    <div className="space-y-6 mt-6">
      {/* Stats Summary */}
      <div className="grid grid-cols-2 gap-3">
        <div className="border border-[#EFEDFB] bg-[#F9F8FE] rounded-lg p-3 flex items-center">
          <div className="bg-[#5652B0] p-2 rounded-md mr-3">
            <PackageCheck size={18} className="text-white" />
          </div>
          <div>
            <span className="text-xs text-gray-600 block">Total Quantity</span>
            <span className="text-[#5652B0] font-semibold text-lg">
              {totalQuantity}
            </span>
          </div>
        </div>

        <div className="border border-[#DCFCE7] bg-[#F0FDF4] rounded-lg p-3 flex items-center">
          <div className="bg-green-600 p-2 rounded-md mr-3">
            <ShoppingCart size={18} className="text-white" />
          </div>
          <div>
            <span className="text-xs text-gray-600 block">
              Discount Offered
            </span>
            <span className="text-green-700 font-semibold text-lg">
              {discountPercentage}%
            </span>
          </div>
        </div>
      </div>

      {/* Brand & Pricing */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="px-4 py-3 bg-[#F9FAFB] border-b border-gray-200">
          <h3 className="font-medium text-gray-800">Product Overview</h3>
        </div>
        <div className="p-4 space-y-3">
          <div className="flex justify-between text-sm border-b border-gray-100 pb-2">
            <span className="text-gray-600">Brand</span>
            <span className="font-medium text-gray-800">{brand}</span>
          </div>
          <div className="flex justify-between text-sm border-b border-gray-100 pb-2">
            <span className="text-gray-600">MRP</span>
            <span className="font-medium text-gray-400 line-through">
              ₹{mrp.toLocaleString()}
            </span>
          </div>
          <div className="flex justify-between text-sm border-b border-gray-100 pb-2">
            <span className="text-gray-600">Selling Price</span>
            <span className="font-medium text-[#5652B0]">
              ₹{sp.toLocaleString()}
            </span>
          </div>
          <div className="flex justify-between text-sm pt-1">
            <span className="text-gray-600">Status</span>
            <span
              className={`font-medium ${
                inStock ? "text-green-600" : "text-red-600"
              }`}>
              {inStock ? "In Stock" : "Out of Stock"}
            </span>
          </div>
        </div>
      </div>

      {/* Size & Quantity Table - Only show if we have size quantities */}
      {sizeQuantities.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          <div className="px-4 py-3 bg-[#F9FAFB] border-b border-gray-200">
            <h3 className="font-medium text-gray-800">Size Distribution</h3>
          </div>
          <TableContainer>
            <Table size="small">
              <TableHead className="bg-[#F9FAFB]">
                <TableRow>
                  <TableCell
                    sx={{
                      fontFamily: "Outfit",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      borderBottom: "1px solid #E5E7EB",
                      color: "#4B5563",
                    }}>
                    Size
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: "Outfit",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      borderBottom: "1px solid #E5E7EB",
                      color: "#4B5563",
                      width: "50%",
                    }}>
                    Quantity
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontFamily: "Outfit",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      borderBottom: "1px solid #E5E7EB",
                      color: "#4B5563",
                      width: "30%",
                    }}>
                    Status
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sizeQuantities.map((row, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      "&:last-child td, &:last-child th": {
                        borderBottom: 0,
                      },
                      "&:hover": {
                        backgroundColor: "#F9FAFB",
                      },
                    }}>
                    <TableCell
                      sx={{
                        fontFamily: "Outfit",
                        borderBottom: "1px solid #E5E7EB",
                        py: 1.5,
                      }}>
                      {row.size}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "Outfit",
                        borderBottom: "1px solid #E5E7EB",
                        py: 1.5,
                      }}>
                      <div className="text-sm">{row.quantity}</div>
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontFamily: "Outfit",
                        borderBottom: "1px solid #E5E7EB",
                        py: 1.5,
                      }}>
                      {row.quantity <= lowStockThreshold ? (
                        <div className="flex items-center justify-end text-amber-600 text-xs">
                          <AlertTriangle size={14} className="mr-1" />
                          Low Stock
                        </div>
                      ) : (
                        <div className="flex items-center justify-end text-green-600 text-xs">
                          <Package size={14} className="mr-1" />
                          In Stock
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      )}

      {/* Stock Status */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="px-4 py-3 bg-[#F9FAFB] border-b border-gray-200">
          <h3 className="font-medium text-gray-800">Stock Status</h3>
        </div>
        <div className="p-4 space-y-3">
          <div className="flex justify-between text-sm border-b border-gray-100 pb-2">
            <span className="text-gray-600 flex items-center">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
              Available
            </span>
            <span className="font-medium text-gray-800">{totalQuantity}</span>
          </div>
          <div className="flex justify-between text-sm border-b border-gray-100 pb-2">
            <span className="text-gray-600 flex items-center">
              <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
              Reserved
            </span>
            <span className="font-medium text-gray-800">0</span>
          </div>
          <div className="flex justify-between text-sm pt-1">
            <span className="text-gray-600 flex items-center">
              <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
              Damaged
            </span>
            <span className="font-medium text-gray-800">0</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductQuickStats;
